import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Web3 } from 'web3';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../utils/api';
// eslint-disable-next-line no-unused-vars
import { toast } from 'react-toastify';
import { NFT_CONTRACT_ADDRESS, NFT_ABI, USDT_DECIMALS } from '../config/contracts';
import '../styles/theme.css';
import './MarketplacePage.css';

// Public BSC RPC endpoints for read-only operations
const BSC_RPC_URLS = [
  'https://bsc-dataseed.bnbchain.org',
  'https://bsc-dataseed1.bnbchain.org',
  'https://bsc-dataseed2.bnbchain.org',
  'https://bsc-dataseed3.bnbchain.org',
  'https://bsc-dataseed1.defibit.io',
  'https://bsc-dataseed2.defibit.io'
];

// Get a Web3 instance for read-only operations (doesn't require wallet connection)
const getReadOnlyWeb3 = () => {
  try {
    // Use first available RPC URL
    return new Web3(BSC_RPC_URLS[0]);
  } catch (error) {
    console.error('Error creating read-only Web3 instance:', error);
    return null;
  }
};

const MarketplacePage = () => {
  // eslint-disable-next-line no-unused-vars
  const { account, isConnected } = useWeb3();
  const [loading, setLoading] = useState(true);
  const [marketplaceData, setMarketplaceData] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [mintPrice, setMintPrice] = useState(null);
  const [presalePrice, setPresalePrice] = useState(null);
  const [publicPrice, setPublicPrice] = useState(null);
  const [presaleActive, setPresaleActive] = useState(null);
  const [publicSaleActive, setPublicSaleActive] = useState(null);
  const [totalSupply, setTotalSupply] = useState(null);
  const [presaleMinted, setPresaleMinted] = useState(null);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [showNFTModal, setShowNFTModal] = useState(false);

  // Fetch contract data
  const fetchContractData = useCallback(async () => {
    if (!NFT_CONTRACT_ADDRESS) return;

    // Always use read-only BSC RPC for marketplace reads (stable and wallet-independent)
    const web3Instance = getReadOnlyWeb3();
    if (!web3Instance) return;

    try {
      const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);

      const safeCall = async (fn, fallback = null) => {
        try {
          return await fn();
        } catch (_) {
          return fallback;
        }
      };

      // Fetch prices, sale status, and minting stats without failing the whole screen
      const [presalePriceWei, publicPriceWei, presaleActiveBool, publicSaleActiveBool, totalSupplyResult, presaleMintedResult] = await Promise.all([
        safeCall(() => nftContract.methods.PRESALE_PRICE().call(), null),
        safeCall(() => nftContract.methods.PUBLIC_PRICE().call(), null),
        safeCall(() => nftContract.methods.presaleActive().call(), null),
        safeCall(() => nftContract.methods.publicSaleActive().call(), null),
        safeCall(() => nftContract.methods.totalSupply().call(), null),
        safeCall(() => nftContract.methods.presaleMinted().call(), null)
      ]);

      // Convert from Wei to USDT (18 decimals)
      const presalePriceFormatted = presalePriceWei != null
        ? parseFloat(presalePriceWei) / Math.pow(10, USDT_DECIMALS)
        : null;
      const publicPriceFormatted = publicPriceWei != null
        ? parseFloat(publicPriceWei) / Math.pow(10, USDT_DECIMALS)
        : null;

      setPresalePrice(presalePriceFormatted);
      setPublicPrice(publicPriceFormatted);

      const toBool = (v) => {
        if (v == null) return null;
        if (typeof v === 'boolean') return v;
        const s = String(v).toLowerCase();
        if (s === 'true' || s === '1') return true;
        if (s === 'false' || s === '0') return false;
        return null;
      };

      const presaleFlag = toBool(presaleActiveBool);
      const publicFlag = toBool(publicSaleActiveBool);
      setPresaleActive(presaleFlag);
      setPublicSaleActive(publicFlag);
      setTotalSupply(totalSupplyResult != null ? parseInt(totalSupplyResult.toString(), 10) : null);
      setPresaleMinted(presaleMintedResult != null ? parseInt(presaleMintedResult.toString(), 10) : null);

      if (presaleFlag === true) {
        setMintPrice(presalePriceFormatted);
      } else if (publicFlag === true) {
        setMintPrice(publicPriceFormatted);
      } else if (presaleFlag === false && publicFlag === false) {
        setMintPrice(null);
      }
    } catch (error) {
      console.error('Error fetching contract data:', error);
    }
  }, []);

  // Fetch NFTs from contract (based on totalSupply) and get their metadata
  const fetchNFTsFromContract = useCallback(async () => {
    if (!totalSupply || totalSupply === 0) {
      setNfts([]);
      return;
    }

    // Always use read-only BSC RPC for marketplace reads
    const web3Instance = getReadOnlyWeb3();
    
    if (!web3Instance || !NFT_CONTRACT_ADDRESS) {
      console.error('Web3 instance or contract address not available');
      return;
    }

    try {
      const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
      
      // Detect tokenId indexing scheme (0-based vs 1-based)
      let startAtZero = false;
      try {
        const owner0 = await nftContract.methods.ownerOf(0).call();
        if (owner0 && owner0 !== '0x0000000000000000000000000000000000000000') {
          startAtZero = true;
        }
      } catch (_) {
        startAtZero = false;
      }
      
      // Create array of token IDs (0..totalSupply-1 OR 1..totalSupply)
      const tokenIds = startAtZero
        ? Array.from({ length: totalSupply }, (_, i) => i)
        : Array.from({ length: totalSupply }, (_, i) => i + 1);
      
      // Fetch metadata and owners for each token ID
      const nftsWithMetadata = await Promise.all(
        tokenIds.map(async (tokenId) => {
          try {
            // Fetch metadata from backend
            const metadataResponse = await api.get(`/nfts/${tokenId}/metadata`);
            const metadata = metadataResponse.data;
            
            // Always fetch owner from chain (this is read-only, doesn't require wallet)
            let owner = '0x0000000000000000000000000000000000000000';
            try {
              const ownerAddress = await nftContract.methods.ownerOf(tokenId).call();
              if (ownerAddress && ownerAddress !== '0x0000000000000000000000000000000000000000') {
                owner = ownerAddress.toLowerCase();
              }
            } catch (ownerError) {
              console.error(`Error fetching owner for token ${tokenId}:`, ownerError);
              // If ownerOf fails, the token might not exist or there's a network issue
            }
            
            return {
              tokenId,
              owner: owner,
              metadata: {
                name: metadata.name || `NFT #${tokenId}`,
                description: metadata.description || '',
                image: metadata.image || `https://phonestream.online/nfts/placeholder.png`,
                attributes: metadata.attributes || []
              }
            };
          } catch (error) {
            console.error(`Error fetching metadata for NFT #${tokenId}:`, error);
            // Still try to get owner even if metadata fails
            let owner = '0x0000000000000000000000000000000000000000';
            try {
              const ownerAddress = await nftContract.methods.ownerOf(tokenId).call();
              if (ownerAddress && ownerAddress !== '0x0000000000000000000000000000000000000000') {
                owner = ownerAddress.toLowerCase();
              }
            } catch (ownerError) {
              console.error(`Error fetching owner for token ${tokenId}:`, ownerError);
            }
            return {
              tokenId,
              owner: owner,
              metadata: {
                name: `NFT #${tokenId}`,
                description: '',
                image: `https://phonestream.online/nfts/placeholder.png`,
                attributes: []
              }
            };
          }
        })
      );

      setNfts(nftsWithMetadata);
    } catch (error) {
      console.error('Error fetching NFTs from contract:', error);
      setNfts([]);
    }
  }, [totalSupply]);

  // Fetch marketplace stats (for volume, recent mints, etc.)
  const fetchMarketplaceData = async () => {
    try {
      const response = await api.get('/nfts/marketplace/stats');
      setMarketplaceData(response.data);
    } catch (error) {
      console.error('Error fetching marketplace data:', error);
      // Don't show error toast for stats, as it's optional
      // Will generate from contract data as fallback
      setMarketplaceData(null);
    }
  };

  // Generate marketplace stats from contract NFTs when API fails
  useEffect(() => {
    if (!marketplaceData && nfts.length > 0 && totalSupply !== null) {
      // Calculate volume
      const totalVolume = nfts.reduce((sum, nft) => {
        const price = nft.tokenId <= 100 ? (presalePrice || 900) : (publicPrice || 1000);
        return sum + price;
      }, 0);

      setMarketplaceData({
        stats: {
          totalMinted: totalSupply,
          totalVolume: totalVolume,
          presaleMinted: nfts.filter(n => n.tokenId <= 100).length,
          publicMinted: nfts.filter(n => n.tokenId > 100).length
        }
      });
    }
  }, [marketplaceData, nfts, totalSupply, presalePrice, publicPrice]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchMarketplaceData(),
        fetchContractData()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchContractData]);

  // Fetch NFTs when totalSupply changes
  useEffect(() => {
    if (totalSupply > 0) {
      fetchNFTsFromContract();
    }
  }, [fetchNFTsFromContract, totalSupply]);

  // Refetch owners if they're still zero addresses (fallback mechanism)
  useEffect(() => {
    if (nfts.length > 0 && totalSupply > 0 && NFT_CONTRACT_ADDRESS) {
      // Check if any NFTs have zero addresses
      const hasZeroOwners = nfts.some(nft => nft.owner === '0x0000000000000000000000000000000000000000');
      
      if (hasZeroOwners) {
        // Always use read-only BSC RPC for marketplace reads
        const web3Instance = getReadOnlyWeb3();
        
        if (web3Instance) {
          const updateOwners = async () => {
            const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
            const updatedNFTs = await Promise.all(
              nfts.map(async (nft) => {
                // Only update if owner is zero address
                if (nft.owner === '0x0000000000000000000000000000000000000000') {
                  try {
                    const ownerAddress = await nftContract.methods.ownerOf(nft.tokenId).call();
                    if (ownerAddress && ownerAddress !== '0x0000000000000000000000000000000000000000') {
                      return { ...nft, owner: ownerAddress.toLowerCase() };
                    }
                  } catch (error) {
                    console.error(`Error fetching owner for token ${nft.tokenId}:`, error);
                  }
                }
                return nft;
              })
            );
            // Only update if we found new owners
            const hasNewOwners = updatedNFTs.some((nft, index) => nft.owner !== nfts[index].owner);
            if (hasNewOwners) {
              setNfts(updatedNFTs);
            }
          };
          updateOwners();
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfts.length, totalSupply]);

  // Format address for display
  const formatAddress = (address) => {
    if (!address || address === '0x0000000000000000000000000000000000000000') {
      return 'Loading...';
    }
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };


  // Show loading only if we have no data at all (contract data should load even without wallet)
  if (loading && totalSupply === null && presaleMinted === null && !marketplaceData) {
    return (
      <div className="marketplace-container">
        <Header />
        <div className="loading-container">
          <div>Loading marketplace...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="marketplace-container">
      <Header />
      
      <div className="marketplace-content">
        <div className="marketplace-header">
          <h1>NFT Marketplace</h1>
          <p className="marketplace-subtitle">Boxphone NFT Collection</p>
        </div>

        {/* Stats Grid */}
        <div className="marketplace-stats-grid">
          <div className="stat-card">
            <h3>Current Mint Price</h3>
            <p className="stat-value">
              {mintPrice !== null ? (
                <>
                  {mintPrice.toLocaleString()} <span className="stat-unit">USDT</span>
                </>
              ) : (
                '—'
              )}
            </p>
            <p className="stat-note">
              {presaleActive === true && 'Presale Active'}
              {publicSaleActive === true && presaleActive !== true && 'Public Sale Active'}
              {presaleActive === false && publicSaleActive === false && 'Sale Not Active'}
              {presaleActive === null && publicSaleActive === null && 'Status Unavailable'}
            </p>
            {presalePrice && publicPrice && (
              <div className="price-breakdown">
                <div>Presale: {presalePrice.toLocaleString()} USDT</div>
                <div>Public: {publicPrice.toLocaleString()} USDT</div>
              </div>
            )}
          </div>

          <div className="stat-card">
            <h3>Total Volume</h3>
            <p className="stat-value">
              {totalSupply !== null && presaleMinted !== null && presalePrice && publicPrice ? (
                <>
                  {((presaleMinted * presalePrice) + ((totalSupply - presaleMinted) * publicPrice)).toLocaleString()} <span className="stat-unit">USDT</span>
                </>
              ) : marketplaceData?.stats?.totalVolume ? (
                <>
                  {marketplaceData.stats.totalVolume.toLocaleString()} <span className="stat-unit">USDT</span>
                </>
              ) : (
                '—'
              )}
            </p>
            <p className="stat-note">All-time sales volume</p>
          </div>

          <div className="stat-card">
            <h3>Total Minted</h3>
            <p className="stat-value">
              {totalSupply !== null ? totalSupply : marketplaceData?.stats?.totalMinted ?? '—'}
            </p>
            <p className="stat-note">of 1,000 NFTs</p>
            {totalSupply !== null && (
              <div className="supply-progress">
                <div 
                  className="supply-progress-bar" 
                  style={{ width: `${(totalSupply / 1000) * 100}%` }}
                />
              </div>
            )}
          </div>

          <div className="stat-card">
            <h3>Presale Minted</h3>
            <p className="stat-value">
              {presaleMinted !== null ? presaleMinted : marketplaceData?.stats?.presaleMinted ?? '—'}
            </p>
            <p className="stat-note">of 100 available</p>
          </div>
        </div>

        {/* NFT Gallery */}
        {nfts.length > 0 && (
          <div className="marketplace-section">
            <h2>All NFTs</h2>
            <div className="nfts-grid">
              {nfts.map((nft) => (
                <div 
                  key={nft._id || nft.tokenId} 
                  className="nft-card"
                  onClick={() => {
                    setSelectedNFT(nft);
                    setShowNFTModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="nft-image">
                    <img 
                      src={nft.metadata?.image || `https://phonestream.online/nfts/placeholder.png`} 
                      alt={nft.metadata?.name || `NFT #${nft.tokenId}`}
                      onError={(e) => {
                        e.target.src = 'https://phonestream.online/nfts/placeholder.png';
                      }}
                    />
                  </div>
                  <div className="nft-info">
                    <h4>{nft.metadata?.name || `NFT #${nft.tokenId}`}</h4>
                    <p>Token ID: #{nft.tokenId}</p>
                    <p className="nft-owner">Owner: {formatAddress(nft.owner)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* NFT Detail Modal */}
      {showNFTModal && selectedNFT && (
        <div className="nft-modal-overlay" onClick={() => setShowNFTModal(false)}>
          <div className="nft-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="nft-modal-close" onClick={() => setShowNFTModal(false)}>
              ×
            </button>
            <div className="nft-modal-image-container">
              <img 
                src={selectedNFT.metadata?.image || `https://phonestream.online/nfts/placeholder.png`} 
                alt={selectedNFT.metadata?.name || `NFT #${selectedNFT.tokenId}`}
                className="nft-modal-image"
                onError={(e) => {
                  e.target.src = 'https://phonestream.online/nfts/placeholder.png';
                }}
              />
            </div>
            <div className="nft-modal-info">
              <h2>{selectedNFT.metadata?.name || `NFT #${selectedNFT.tokenId}`}</h2>
              <div className="nft-modal-details">
                <div className="nft-modal-detail-item">
                  <span className="detail-label">Token ID:</span>
                  <span className="detail-value">#{selectedNFT.tokenId}</span>
                </div>
                <div className="nft-modal-detail-item">
                  <span className="detail-label">Owner:</span>
                  <span className="detail-value" title={selectedNFT.owner}>
                    {formatAddress(selectedNFT.owner)}
                  </span>
                </div>
                {selectedNFT.metadata?.description && (
                  <div className="nft-modal-detail-item full-width">
                    <span className="detail-label">Description:</span>
                    <p className="detail-description">{selectedNFT.metadata.description}</p>
                  </div>
                )}
                {selectedNFT.metadata?.attributes && selectedNFT.metadata.attributes.length > 0 && (
                  <div className="nft-modal-detail-item full-width">
                    <span className="detail-label">Attributes:</span>
                    <div className="nft-attributes">
                      {selectedNFT.metadata.attributes.map((attr, index) => (
                        <div key={index} className="attribute-item">
                          <span className="attribute-trait">{attr.trait_type}:</span>
                          <span className="attribute-value">{attr.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MarketplacePage;
